<template>
  <CustomTitleBar :title="currentStep === 1 ? '设置支付密码' : '重置支付密码'" />
  <div class="page-content">
    <!-- 密码设置步骤 -->
    <template v-if="!showForgetForm">
      <div class="pwd-input-wrap">
        <!-- 第一步：输入原密码 -->
        <template v-if="currentStep === 1">
          <div class="tip-text">请输入支付密码以验证身份</div>
          <van-password-input :value="oldPassword" :focused="showKeyboard" :length="6" @focus="onFocus" />
        </template>

        <!-- 第二步：设置新密码 -->
        <template v-if="currentStep === 2">
          <div class="tip-text">请设置支付密码，用于支付验证</div>
          <van-password-input :value="password" :focused="showKeyboard" :length="6" @focus="onFocus" />
        </template>

        <!-- 第三步：确认新密码 -->
        <template v-if="currentStep === 3">
          <div class="tip-text">请再次填写支付密码以确认</div>
          <van-password-input :value="confirmPassword" :focused="showKeyboard" :length="6" @focus="onFocus" />
        </template>
      </div>

      <div class="btn-wrap">
        <van-button type="primary" round @click="handleNext" :disabled="!isValid">
          {{ currentStep === 1 ? '继续' : '确认' }}
        </van-button>
        <div class="forget-pwd" v-if="currentStep === 1" @click="showForgetForm = true">
          忘记密码
        </div>
      </div>

      <!-- 数字键盘 -->
      <van-number-keyboard :show="showKeyboard" @blur="onBlur" @input="onInput" @delete="onDelete" :maxlength="6" />
    </template>

    <!-- 忘记密码表单 -->
    <template v-else>
      <van-form @submit="handleForgetSubmit" ref="formRef">
        <!-- 手机号输入框 -->
        <div class="input-group">
          <van-field v-model="phoneNumber" placeholder="输入手机号" :border="false" :rules="[
            { required: true, message: '请输入手机号' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
          ]">
            <template #label>
              <div class="country-code">
                <SvgIcon name="phone" />
                <span>+{{ phoneAreaPrefix }}</span>
                <van-icon name="arrow-down" size="14" color="#666666" />
              </div>
            </template>
          </van-field>
        </div>

        <!-- 新密码输入 -->
        <div class="input-group">
          <van-field v-model="newPassword" type="password" placeholder="输入新的六位数支付密码" :border="false" maxlength="6"
            :rules="[{ required: true, message: '请输入支付密码' }, { pattern: /^\d{6}$/, message: '请输入6位数字密码' }]">
            <template #label>
              <SvgIcon name="pwd" />
            </template>
          </van-field>
        </div>

        <!-- 确认新密码 -->
        <div class="input-group">
          <van-field v-model="confirmNewPassword" type="password" placeholder="确认新的六位数支付密码" :border="false"
            maxlength="6" :rules="[
              { required: true, message: '请再次输入支付密码' },
              { validator: validateConfirmPassword, message: '两次输入的密码不一致' }
            ]">
            <template #label>
              <SvgIcon name="pwd" />
            </template>
          </van-field>
        </div>

        <!-- 图形验证码输入框 -->
        <div class="input-group">
          <van-field v-model="imageCode" placeholder="输入图形验证码" :border="false"
            :rules="[{ required: true, message: '请输入图形验证码' }]">
            <template #label>
              <SvgIcon name="code" />
            </template>
            <template #right-icon>
              <el-image class="captcha-img" :src="captchaUrl" @click="refreshCaptcha" alt="验证码" />
            </template>
          </van-field>
        </div>

        <!-- 验证码输入框 -->
        <div class="input-group">
          <van-field v-model="verificationCode" placeholder="输入验证码" :border="false"
            :rules="[{ required: true, message: '请输入验证码' }]">
            <template #label>
              <SvgIcon name="code2" />
            </template>
            <template #right-icon>
              <van-button size="small" plain type="primary" @click="sendVerificationCode" :disabled="cooldownTime > 0">
                {{ cooldownTime > 0 ? `${cooldownTime}s` : '获取验证码' }}
              </van-button>
            </template>
          </van-field>
        </div>

        <!-- 按钮区域 -->
        <div class="forget-btn-wrap">
          <van-button type="primary" block round native-type="submit" @click="handleForgetSubmit">确认</van-button>
          <div class="back-btn" @click="handleBack">返回</div>
        </div>
      </van-form>
    </template>
  </div>
</template>

<script lang="ts" setup>
import CustomTitleBar from "@/components/CustomTitleBar/index.vue";
import { ref, computed, onMounted } from "vue";
import { showToast } from 'vant';
import { useRouter } from 'vue-router';
import { sendSmsCode, getCaptcha } from '@/api/system';
import { resetPayPassword } from '@/api/wallet';

const router = useRouter();
// 密码设置相关
const currentStep = ref(1);
const oldPassword = ref("");
const password = ref("");
const confirmPassword = ref("");
const showKeyboard = ref(false);
const showForgetForm = ref(false);

// 忘记密码表单相关
const phoneAreaPrefix = ref('86');
const phoneNumber = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const verificationCode = ref('');
const cooldownTime = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

const formRef = ref();

// 计算按钮是否可用
const isValid = computed(() => {
  switch (currentStep.value) {
    case 1:
      return oldPassword.value.length === 6;
    case 2:
      return password.value.length === 6;
    case 3:
      return confirmPassword.value.length === 6;
    default:
      return false;
  }
});

// 密码输入相关方法
const onFocus = () => {
  showKeyboard.value = true;
};

const onBlur = () => {
  showKeyboard.value = false;
};

const onInput = (key: string) => {
  switch (currentStep.value) {
    case 1:
      if (oldPassword.value.length < 6) {
        oldPassword.value += key;
      }
      break;
    case 2:
      if (password.value.length < 6) {
        password.value += key;
      }
      break;
    case 3:
      if (confirmPassword.value.length < 6) {
        confirmPassword.value += key;
      }
      break;
  }
};

const onDelete = () => {
  switch (currentStep.value) {
    case 1:
      if (oldPassword.value.length > 0) {
        oldPassword.value = oldPassword.value.slice(0, -1);
      }
      break;
    case 2:
      if (password.value.length > 0) {
        password.value = password.value.slice(0, -1);
      }
      break;
    case 3:
      if (confirmPassword.value.length > 0) {
        confirmPassword.value = confirmPassword.value.slice(0, -1);
      }
      break;
  }
};

// 添加密码验证方法
const validateConfirmPassword = (value: string) => {
  return value === newPassword.value;
};

// 返回按钮处理
const handleBack = () => {
  showForgetForm.value = false;
  // 重置表单数据
  phoneNumber.value = '';
  newPassword.value = '';
  confirmNewPassword.value = '';
  verificationCode.value = '';
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  cooldownTime.value = 0;
};

// 图形验证码相关
const imageCode = ref('');
const captchaUrl = ref('');

// 获取图形验证码
const refreshCaptcha = async () => {
  try {
    const res = await getCaptcha();
    captchaUrl.value = URL.createObjectURL(res);
  } catch (error) {
    console.log('获取图形验证码失败', error);
  }
};

// 在组件挂载时获取图形验证码
onMounted(() => {
  refreshCaptcha();
});

// 修改发送验证码前校验
const sendVerificationCode = async () => {
  try {
    // 校验手机号和图形验证码
    await formRef.value?.validate(['phoneNumber', 'imageCode']);

    const res = await sendSmsCode({
      captcha: imageCode.value,
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      type: 3
    });

    if (res.code === 200) {
      showToast('发送成功');
      cooldownTime.value = 60;
      timer = setInterval(() => {
        cooldownTime.value--;
        if (cooldownTime.value <= 0 && timer) {
          clearInterval(timer);
          timer = null;
        }
      }, 1000);
    } else {
      showToast(res.msg);
      // 刷新图形验证码
      refreshCaptcha();
    }
  } catch (error) {
    console.log('验证失败', error);
  }
};

// 提交表单
const handleForgetSubmit = async () => {
  try {
    await formRef.value?.validate();
    // TODO: 处理忘记密码提交逻辑
    const res = await resetPayPassword({
      captcha: verificationCode.value,
      newPassword: newPassword.value,
      phoneAreaPrefix: phoneAreaPrefix.value,
      phoneNumber: phoneNumber.value,
      type: 1,
    });
    if (res.code === 200) {
      showToast({
        type: 'success',
        message: '重置支付密码成功'
      });
      router.go(-1);
    } else {
      showToast(res.msg);
    }
  } catch (error) {
    console.log('表单验证失败', error);
  }
};

const handleNext = async () => {
  if (currentStep.value === 1) {
    // TODO: 验证原密码是否正确
    if (oldPassword.value.length === 6) {
      currentStep.value = 2;
      showKeyboard.value = false;
    }
  } else if (currentStep.value === 2) {
    if (password.value.length === 6) {
      currentStep.value = 3;
      showKeyboard.value = false;
    }
  } else {
    if (confirmPassword.value !== password.value) {
      showToast({
        type: 'fail',
        message: '两次输入的密码不一致'
      });
      confirmPassword.value = '';
      return;
    }

    try {
      const res = await resetPayPassword({
        newPassword: password.value,
        oldPassword: oldPassword.value,
        type: 0,
      });
      if (res.code === 200) {
        showToast({
          type: 'success',
          message: '重置支付密码成功'
        });
        router.go(-1);
      } else {
        showToast(res.msg);
      }
    } catch (error) {
      console.log('重置支付密码失败', error);
    }
  }
};
</script>

<style lang="scss" scoped>
.page-content {
  padding: 64px 20px;
  box-sizing: border-box;

  .pwd-input-wrap {
    .tip-text {
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
      text-align: center;
    }

    :deep(.van-password-input) {
      .van-password-input__security {
        justify-content: space-between;

        .van-password-input__item {
          width: 40px;
          height: 40px;
          background: #E5E5E5;
          border-radius: 8px;
          flex: none;
        }
      }
    }
  }

  .btn-wrap {
    margin-top: 40px;
    text-align: center;

    .van-button {
      width: 90%;
      background-color: $primary-color;
      border-color: $primary-color;
    }

    .forget-pwd {
      margin-top: 16px;
      color: $primary-color;
      font-size: 14px;
    }
  }

  // 忘记密码表单样式
  .input-group {
    background: transparent;
    border-bottom: 1px solid #ebedf0;
    border-radius: 0;
    margin-bottom: 16px;
    overflow: hidden;

    .captcha-img {
      width: 100px;
      height: 32px;
      cursor: pointer;
    }

    :deep(.van-field) {
      background: transparent;

      &::after {
        display: none;
      }
    }

    :deep(.van-field__label) {
      width: auto;
      display: flex;
      align-items: center;
    }
  }

  .country-code {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    border-right: 1px solid #ebedf0;
    margin-right: 8px;

    span {
      font-size: 14px;
      color: #333;
      font-weight: normal;
    }
  }

  .forget-btn-wrap {
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .van-button {
      width: 90%;
      background-color: $primary-color;
      border-color: $primary-color;
    }

    .back-btn {
      margin-top: 16px;
      color: $primary-color;
      font-size: 14px;
    }
  }
}
</style>